import { useEffect, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import type { Feature, Point } from 'geojson';
import Supercluster from 'supercluster';
import { getLeaflet } from './leafletHelpers';
import { COMMUNITY_BADGE_PATH, ON_COLOR } from '../../theme';
import { createPrefixedLogger } from '../../utils/logger';
import { clusterBubbleSize } from './markerSizes';
import { MAX_ZOOM } from './types';
import { communityProgramPopupHtml } from './communityProgramPopup';
import {
  COMMUNITY_PROGRAMS,
  type CommunityProgramId,
  type CommunityProgramProperties,
} from './communityPrograms';

const log = createPrefixedLogger('map:community-programs');

const MARKER_SIZE = 18;
const BADGE_SIZE = 11;

interface PointProperties extends Record<string, unknown> {
  sourceId: number;
}

type ProgramFeature = Feature<Point, CommunityProgramProperties>;

interface ProgramFeatureCollection {
  type: 'FeatureCollection';
  features: ProgramFeature[];
}

/**
 * Render one community program point layer (Firewise USA sites, California
 * fire safe councils), clustered, shown and hidden by `enabled`.
 *
 * Different in shape from useFhszOverlay/useFireHistoryOverlay in three ways
 * that are why this is its own hook and not a fourth copy of theirs: the file
 * is not keyed by service area, the geometry is points rather than polygon
 * fills, and the markers cluster. Those two polygon hooks carry a note
 * suggesting they fold together some day; this is not that third case, so
 * they are left alone.
 *
 * Deliberately NOT gated by STRUCTURE_RENDER_MIN_ZOOM_WEB. A national view at
 * zoom 3 is the whole point of the layer.
 */
export function useCommunityProgramOverlay({
  mapRef,
  isLoaded,
  programId,
  enabled,
  color,
}: {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
  programId: CommunityProgramId;
  enabled: boolean;
  /** Theme token, passed in because hooks below the React tree cannot read
   *  the theme themselves. Same arrangement useMarkerLayer uses. */
  color: string;
}): void {
  const program = COMMUNITY_PROGRAMS[programId];
  const featuresRef = useRef<ProgramFeature[] | null>(null);
  const indexRef = useRef<Supercluster<PointProperties> | null>(null);
  const groupRef = useRef<L.LayerGroup | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const loadingRef = useRef(false);
  const failedRef = useRef(false);
  const colorRef = useRef(color);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !enabled) return;
    const L = getLeaflet();

    const pane = map.getPane(program.pane) ?? map.createPane(program.pane);
    pane.style.zIndex = program.paneZIndex;
    pane.style.display = '';

    if (!groupRef.current) {
      groupRef.current = L.layerGroup().addTo(map);
    }

    // A theme flip changes every icon, and icons are cached by key, so drop
    // what is drawn and let the render below rebuild it. Done here rather
    // than in its own effect so the clear always happens BEFORE the render,
    // not after it, which would leave an empty layer until the next pan.
    if (colorRef.current !== color) {
      colorRef.current = color;
      groupRef.current.clearLayers();
      markersRef.current.clear();
    }

    const render = () => {
      const index = indexRef.current;
      const group = groupRef.current;
      const features = featuresRef.current;
      if (!index || !group || !features) return;
      const bounds = map.getBounds();
      const clusters = index.getClusters(
        [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
        Math.round(map.getZoom())
      );

      // Clicking a cluster zooms to the point it breaks apart, the same as a
      // structure cluster (useMarkerLayer.web.ts). getClusterExpansionZoom
      // throws on a cluster with nothing left to expand, hence the fallback.
      const bindZoom = (
        marker: L.Marker,
        clusterId: number | undefined,
        lat: number,
        lng: number
      ) => {
        // Rebound on every render, never only on creation: the marker cache
        // key is position and count, so a reused marker can be holding a
        // cluster_id from another zoom, and a supercluster id encodes the
        // zoom it came from.
        marker.off('click');
        marker.on('click', () => {
          const fallback = Math.min(map.getZoom() + 2, MAX_ZOOM);
          let target = fallback;
          if (clusterId !== undefined) {
            try {
              target = Math.min(index.getClusterExpansionZoom(clusterId), MAX_ZOOM);
            } catch {
              target = fallback;
            }
          }
          map.setView([lat, lng], target);
        });
      };

      const seen = new Set<string>();
      for (const cluster of clusters) {
        const [lng, lat] = cluster.geometry.coordinates;
        const props = cluster.properties as PointProperties & {
          cluster?: boolean;
          cluster_id?: number;
          point_count?: number;
        };
        const isCluster = props.cluster === true;
        const key = isCluster
          ? `c:${lat.toFixed(4)}:${lng.toFixed(4)}:${props.point_count ?? 0}`
          : `p:${props.sourceId}`;
        seen.add(key);
        const existing = markersRef.current.get(key);
        if (existing) {
          if (isCluster) bindZoom(existing, props.cluster_id, lat, lng);
          continue;
        }

        let marker: L.Marker;
        if (isCluster) {
          marker = L.marker([lat, lng], {
            icon: clusterIcon(L, props.point_count ?? 0, color),
            pane: program.pane,
            keyboard: false,
          });
          bindZoom(marker, props.cluster_id, lat, lng);
        } else {
          marker = L.marker([lat, lng], {
            icon: pointIcon(L, program.badge, color),
            pane: program.pane,
            keyboard: false,
          }).bindPopup(communityProgramPopupHtml(programId, features[props.sourceId].properties), {
            closeButton: true,
            autoPan: true,
          });
        }
        marker.addTo(group);
        markersRef.current.set(key, marker);
      }

      for (const [key, marker] of markersRef.current) {
        if (seen.has(key)) continue;
        group.removeLayer(marker);
        markersRef.current.delete(key);
      }
    };

    const build = (collection: ProgramFeatureCollection) => {
      featuresRef.current = collection.features;
      // A few thousand points index in single-digit milliseconds, so unlike
      // useClusterIndex (tens of thousands of structures) there is nothing
      // here worth deferring to idle time.
      const index = new Supercluster<PointProperties>({
        radius: program.clusterRadius,
        maxZoom: MAX_ZOOM,
        minPoints: 3,
      });
      index.load(
        collection.features.map((feature, sourceId) => ({
          type: 'Feature' as const,
          geometry: feature.geometry,
          properties: { sourceId },
        }))
      );
      indexRef.current = index;
      render();
    };

    map.on('moveend', render);
    map.on('zoomend', render);

    let cancelled = false;
    if (featuresRef.current) {
      render();
    } else if (!loadingRef.current && !failedRef.current) {
      loadingRef.current = true;
      fetch(program.url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<ProgramFeatureCollection>;
        })
        .then((collection) => {
          if (cancelled || !Array.isArray(collection?.features)) return;
          build(collection);
        })
        .catch((error) => {
          // Remembered, so a toggle that failed once does not refetch a
          // missing export on every flip.
          failedRef.current = true;
          log.warn(`Failed to load the ${program.id} layer`, error);
        })
        .finally(() => {
          loadingRef.current = false;
        });
    }

    return () => {
      cancelled = true;
      map.off('moveend', render);
      map.off('zoomend', render);
    };
  }, [mapRef, isLoaded, enabled, programId, program, color]);

  // Hide (never remove) once disabled, so re-enabling is instant and never
  // refetches, the same arrangement the hazard and history overlays use.
  useEffect(() => {
    if (enabled) return;
    const pane = mapRef.current?.getPane(program.pane);
    if (pane) pane.style.display = 'none';
  }, [enabled, mapRef, program.pane]);
}

function pointIcon(L: typeof import('leaflet'), badge: string, color: string): L.DivIcon {
  // The hexagon path comes from the theme and the hue is passed in, so no
  // color literal lives in this file. White is the marker outline in both
  // themes, the same as every other pin on this map (ON_COLOR.fill).
  const hex = `<svg width="${MARKER_SIZE}" height="${MARKER_SIZE}" viewBox="0 0 24 24" fill="${color}" stroke="${ON_COLOR.fill}" stroke-width="2" stroke-linejoin="round" style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));"><path d="${COMMUNITY_BADGE_PATH}"/></svg>`;
  const badgeHtml = `<div style="position:absolute;bottom:-3px;right:-3px;width:${BADGE_SIZE}px;height:${BADGE_SIZE}px;border-radius:50%;background:${ON_COLOR.fill};color:${color};font-size:8px;font-weight:700;line-height:${BADGE_SIZE}px;text-align:center;box-shadow:0 1px 2px rgba(0,0,0,0.3);">${badge}</div>`;
  return L.divIcon({
    className: 'cp-marker',
    html: `<div style="position:relative;overflow:visible;width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;">${hex}${badgeHtml}</div>`,
    iconSize: [MARKER_SIZE, MARKER_SIZE],
    iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE / 2],
  });
}

/**
 * Bubble standing for several recognized communities. Mirrors the
 * observation cluster's halo-and-core build so a cluster reads as the same
 * kind of thing as the markers it stands for, in the layer's own color.
 */
function clusterIcon(L: typeof import('leaflet'), count: number, color: string): L.DivIcon {
  const size = clusterBubbleSize(count);
  const inner = size - 10;
  const halo = `display:flex;align-items:center;justify-content:center;border-radius:50%;background:${color}38;`;
  const core = `border-radius:50%;border:2px solid ${ON_COLOR.fill};background:${color}EB;box-shadow:0 2px 6px rgba(0,0,0,0.35);`;
  return L.divIcon({
    className: 'cp-cluster',
    html: `<div style="${halo}width:${size}px;height:${size}px;"><div style="${core}width:${inner}px;height:${inner}px;"></div></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
